import { prop, find, propEq } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { useState } from 'react'
import { Avatar } from '@/components/avatar'
import CloseButton from '@/components/close-button'
import {
  ChatPhone,
  Email,
  EmailDisabled,
  PhoneDisabled,
  Gear,
  QuestionMark
} from '@/components/icons'
import { createModal, useModalize } from '@/components/modalize'
import Tooltip from '@/components/custom-tooltip'
import VerifiedBadge from '@/components/verified-badge'
import { showTenantProfile } from '@/features/Profile/TenantProfileModal'
import useRole from '@/hooks/use-role'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import formatName from '@/utils/format-name'
import { getImageUrl } from '@/utils/image'
import isVerified from '@/utils/is-verified'
import { showLandlordProfile } from '@/features/Profile/LandlordProfileModal'
import { showHidePhoneModal } from '../chat-room/hide-phone-modal'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-height: 90vh;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    max-height: unset;
    max-height: calc(100% - 64px);
  }
`

const Header = styled.div`
  height: 60px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 25px 0;
  flex-shrink: 0;
  margin-bottom: 25px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    justify-content: flex-start;
    height: 50px;
    padding: 5px 20px 0;
  }
`

const ContentWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px;

  // use after for bottom padding due to Firefox bug:
  // https://stackoverflow.com/questions/29986977
  :after {
    content: '';
    display: block;
    padding-bottom: 24px;
    width: 100%;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 0 16px;
  }
`

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 760px;
  width: 100%;
`

const Title = styled.h1`
  font-size: 16px;
  line-height: 27px;
  font-weight: ${fontWeights.bold};
`

const CardsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 20px;
  flex-direction: column;
  width: 100%;
`

const LandlordsCard = styled.div`
  border-radius: 10px;
  width: 100%;
  flex: 1;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0 auto;
    grid-row: unset;
  }
`

const RentersCard = styled(LandlordsCard)`
  ${props =>
    props.reverse &&
    `
  grid-row: 1;
`}
`

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 12px;
`

const CardTitle = styled.h2`
  font-size: 16px;
  line-height: 20px;
  font-weight: ${fontWeights.medium};
  margin: 0;
`

const LandlordsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Address = styled.span`
  font-size: 12px;
  max-width: 190px;
  color: ${colors.grey32};
  margin-top: 4px;
`

const MemberWrapper = styled.div`
  display: flex;
  align-items: center;
  border-left: 2px solid rgba(0, 0, 0, 0.1);
  padding: 18px 0;
  padding-left: 18px;
  position: relative;

  :not(:last-child) {
    padding-bottom: 30px;
  }
`

const MemberInfoWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 20px;
`

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: 190px;
`

const NameText = styled.p`
  font-size: ${fontSizes.regular};
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 22px;
  letter-spacing: -0.2px;
  margin: 0;
  max-width: 150px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const PrimaryText = styled.p`
  font-size: 12px;
  color: ${colors.grey32};
  line-height: 17px;
  margin: 0;
`

const IconsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90px;
`

const AvatarWrapper = styled.div`
  cursor: pointer;
`

const StyledGearIcon = styled(Gear)`
  margin-right: 11px;
`

const ManageButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: ${fontWeights.bold};
  color: ${colors.blue16};
  background: transparent;
  border: 1.5px solid ${colors.grey88};
  border-radius: 6px;
  cursor: pointer;
  padding: 8px 0;
  margin-top: 20px;
  :hover {
    opacity: 0.7;
  }
`

const ContactInfoContainer = styled.div`
  display: flex;
  align-items: center;
`

const ContactInfoText = styled.span`
  font-size: 14px;
  color: ${colors.grey56};
  margin-right: 5px;
`

const StyledQuestionMark = styled(QuestionMark)`
  cursor: pointer;
`

const UserWrapper = styled.div`
  position: relative;
`

const HideText = styled.span`
  position: absolute;
  bottom: 0;
  right: ${props => (props.textIsHide ? '5px' : '0px')};
  font-size: 10px;
  z-index: 1;
  color: ${colors.link};
  cursor: pointer;
  :hover {
    opacity: 0.7;
  }
`

const Member = ({ user, isPrimary, isLocked, isPhoneHidden }) => {
  const { t } = useTranslation()
  const verified = isVerified(user, user.company)
  const number = user?.phone_number
  const email = user.username

  const handleViewProfile = () => {
    if (user?.company?.operation_type === 'landlord') {
      showLandlordProfile({
        data: user
      })
    } else {
      showTenantProfile({
        data: user
      })
    }
  }

  return (
    <MemberWrapper>
      <AvatarWrapper onClick={handleViewProfile}>
        <Avatar
          location={getImageUrl(user.avatar?.aws_s3_key, {
            width: 80,
            height: 80
          })}
          width={40}
          height={40}
          shadow
        />
      </AvatarWrapper>
      <MemberInfoWrapper>
        <NameWrapper>
          <NameText>{formatName(user)}</NameText>
          <VerifiedBadge verified={verified} type="id" width={18} height={18} left={2} />
        </NameWrapper>
        {isPrimary && (
          <PrimaryText>{t('b.chat.view.primary_landlord.text', 'Primary Landlord')}</PrimaryText>
        )}
      </MemberInfoWrapper>
      <IconsWrapper>
        {!!email && !isLocked ? (
          <a href={`mailto:${email}`}>
            <Email />
          </a>
        ) : (
          <EmailDisabled />
        )}
        {!isLocked && !isPhoneHidden ? (
          <a href={`tel:${number}`}>
            <ChatPhone />
          </a>
        ) : (
          <PhoneDisabled />
        )}
      </IconsWrapper>
    </MemberWrapper>
  )
}

const TimelineMembersModal = ({ timeline, isLocked, isRented, history, address }) => {
  const { t } = useTranslation()
  const role = useRole()
  const isLandlord = role === 'landlord'
  const landlords = isLandlord ? timeline.users.us : timeline.users.them
  const session = useSelector(prop('session')) || {}
  const userId = session.session?.id
  const currentLandlordUser = isLandlord ? find(propEq('id', userId))(landlords) : null
  const [isPhoneHidden, setIsPhoneHidden] = useState(
    currentLandlordUser?.hide_phone_number === 1 ? true : false
  )
  const { close } = useModalize()
  const renters = isLandlord ? timeline.users.them : timeline.users.us
  const primaryLandlordId = isLandlord
    ? timeline.primary_user_ids.us
    : timeline.primary_user_ids.them

  const currentUserIsPrimaryLandlord = userId === primaryLandlordId

  const contactInfoText = {
    tenant: {
      landlordText: t(
        'b.chat.view.tenant_role_landlord_text.text',
        'Only available if the landlord has opted to share their phone number.'
      ),
      renterText: t(
        'b.chat.view.tenant_role_tenant_text.text',
        'Your contact details are only shared with landlords if you have applied to their listing or if you are in an active tenancy agreement.'
      )
    },
    landlord: {
      landlordText: t(
        'b.chat.view.landlord_role_landlord_text.text',
        'Your contact details are shared with additional landlords in this listing and only shared with renters if you have not opted to hide from public.'
      ),
      renterText: t(
        'b.chat.view.landlord_role_tenant_text.text',
        'Renter contact information is only available if they have applied to your listing or if they are in an active tenancy agreement.'
      )
    }
  }

  const handleManageClick = () => {
    history.push(`/manage/listings/${timeline.listing_id}`)
    close()
  }

  return (
    <Wrapper>
      <Header>
        <Title>{t('b.chat.view.members.title', 'Members')}</Title>
        <CloseButton onClick={close} />
      </Header>
      <ContentWrapper>
        <ContentInner>
          <CardsWrapper>
            <LandlordsCard>
              <TitleWrapper>
                <LandlordsContainer>
                  <CardTitle>{t('b.chat.view.landlords.title', 'Landlords')}</CardTitle>
                  <Address>{address}</Address>
                </LandlordsContainer>
                <ContactInfoContainer>
                  <ContactInfoText>
                    {t('b.chat.view.contact_info.text', 'Contact Info')}
                  </ContactInfoText>
                  <Tooltip
                    openOnMobilePress
                    placement="bottom"
                    text={contactInfoText[role].landlordText}
                  >
                    <StyledQuestionMark />
                  </Tooltip>
                </ContactInfoContainer>
              </TitleWrapper>
              {landlords.map(landlord => {
                return (
                  <UserWrapper isPhoneHidden={isPhoneHidden} key={`landlord-${landlord.id}`}>
                    {landlord.id === userId && (
                      <HideText
                        textIsHide={!isPhoneHidden}
                        onClick={() =>
                          showHidePhoneModal({
                            hidden: isPhoneHidden,
                            setIsPhoneHidden,
                            closeTimelineMembersModal: close
                          })
                        }
                      >
                        {isPhoneHidden
                          ? t('b.chat.view.unhide.button', 'Unhide')
                          : t('b.chat.view.hide.button', 'Hide')}
                      </HideText>
                    )}
                    <Member
                      isPhoneHidden={
                        !!currentLandlordUser ? isPhoneHidden : landlord?.hide_phone_number === 1
                      }
                      key={landlord.id}
                      user={landlord}
                      isPrimary={landlord.id === primaryLandlordId}
                      isLocked={isLocked}
                      isRented={isRented}
                    />
                  </UserWrapper>
                )
              })}
            </LandlordsCard>
            <RentersCard reverse={isLandlord}>
              <TitleWrapper>
                <CardTitle>{t('b.chat.view.renters.title', 'Renters')}</CardTitle>
                <ContactInfoContainer>
                  <ContactInfoText>
                    {t('b.chat.view.contact_info.text', 'Contact Info')}
                  </ContactInfoText>
                  <Tooltip
                    openOnMobilePress
                    placement="bottom"
                    text={contactInfoText[role].renterText}
                  >
                    <StyledQuestionMark />
                  </Tooltip>
                </ContactInfoContainer>
              </TitleWrapper>
              {renters.map(renter => {
                return (
                  <UserWrapper key={`renter-${renter.id}`}>
                    <Member
                      user={renter}
                      isLocked={isLocked}
                      isRented={isRented}
                      userData={renter}
                    />
                  </UserWrapper>
                )
              })}
            </RentersCard>
          </CardsWrapper>
          {currentUserIsPrimaryLandlord && (
            <ManageButton onClick={handleManageClick}>
              <StyledGearIcon />
              <span>{t('l.chat.view.manage_landlords.button', 'Manage Landlords')}</span>
            </ManageButton>
          )}
        </ContentInner>
      </ContentWrapper>
    </Wrapper>
  )
}

export const show = createModal(TimelineMembersModal, {
  width: 'auto',
  maxWidth: '448px',
  flexGrow: true,
  border: '1px solid #d8d8d8',
  mobileAlign: 'flex-end',
  tabletFullWidth: true
})
